import {createApi} from "@reduxjs/toolkit/query/react";
import axiosBaseQuery from "../axiosBaseQuery";
import {SimplePaletteColorOptions} from "@mui/material";
import {SymbolCodepoints} from "react-material-symbols";
import {
  AttemptStatusType,
  FilterType,
  ProblemDifficultyType,
  ProblemType,
  stringToAttemptStatusType,
  stringToProblemDifficultyType,
} from "../../config/enums";
import {statusTheme} from "../../config/themes/status-theme";
import {statusIcons} from "../../assets/icons/ProblemIcons";
import {difficultyTheme} from "../../config/themes/difficulty-theme";
import {PyqMockDropdownYearListType} from "../../features/mock-test-table/components/search-dropdown/PyqMockDropdownUI";
import {ViewModelProps} from "../../config/type";
import {QuestionCategoryType} from "../../features/problemset-table-v2/data/question-category-data";

// Response types from API
type NameIdApiType = {
  name: string;
  reference_id: string;
  slug: string;
  metadata?: {
    icon?: SymbolCodepoints;
    color?: SimplePaletteColorOptions;
    altText?: string;
  };
};

type ChapterApiType = {
  questions_count: number;
  topics_count: number;
} & NameIdApiType;

type GetExamConfigApiResponse = {
  subjects?: NameIdApiType[];
  chapters?: Record<string, ChapterApiType[]>;
  topics?: Record<string, NameIdApiType[]>;
  question_types?: NameIdApiType[];
  user_question_status?: NameIdApiType[];
  difficulty_types?: NameIdApiType[];
  previous_years?: {id: string; label: string; description: string}[];
};

// Transformed response type
export type IGetExamConfig = {
  subjects: {id: string; viewModel: ViewModelProps}[];
  chapters: Record<
    string,
    {
      id: string;
      viewModel: ViewModelProps;
      questionsCount: number;
      topicsCount: number;
    }[]
  >;
  topics: Record<string, {id: string; viewModel: ViewModelProps}[]>;
  patterns: {id: string; viewModel: ViewModelProps}[];
  attemptStatus: {id: string; viewModel: ViewModelProps}[];
  difficulty: {id: string; viewModel: ViewModelProps}[];
  pyqMock: {
    years: PyqMockDropdownYearListType[];
  };
  problemsetFilters: {
    subjects: {
      subjectId: string;
      isSelected: boolean;
      chapters: {
        chapterId: string;
        isSelected: boolean;
        [FilterType.PAGE]: number;
        [FilterType.ATTEMPT_STATUS]: AttemptStatusType[];
        [FilterType.DIFFICULTY]: ProblemDifficultyType[];
        [FilterType.PATTERN]: ProblemType[];
        [FilterType.FEATURE]: QuestionCategoryType;
      }[];
    }[];
  };
};

function parseGetExamConfigResponse(
  res: GetExamConfigApiResponse,
): IGetExamConfig {
  const subjects = getSubjects(res);
  const chapters = getChapters(res);
  const topics = {};
  const patterns = getPatterns(res);
  const difficulty = getDifficulty(res);
  const attemptStatus = getAttemptStatus(res);

  const problemsetFiltersStore = localStorage.getItem("problemsetFilters");
  let psFilters: any = null;
  try {
    psFilters = problemsetFiltersStore
      ? JSON.parse(problemsetFiltersStore)
      : null;
  } catch (error) {
    psFilters = null;
  }

  function getProblemsetFilters(): IGetExamConfig["problemsetFilters"] {
    // Helper function to check if any subject is selected in stored filters
    const hasSelectedSubject = (): boolean => {
      return (
        Array.isArray(psFilters?.subjects) &&
        psFilters.subjects.some((subject: any) => subject.isSelected)
      );
    };

    // Helper function to check if any chapter is selected for a subject
    const hasSelectedChapter = (subjectFilters: any): boolean => {
      return (
        Array.isArray(subjectFilters?.chapters) &&
        subjectFilters.chapters.some((chapter: any) => chapter.isSelected)
      );
    };

    // Helper function to get default selected subject
    const getDefaultSubjectId = (): string => {
      return subjects.length > 0 ? subjects[0].id : "";
    };

    // Helper function to get default selected chapter for a subject
    const getDefaultChapterId = (subjectId: string): string => {
      const subjectChapters = chapters[subjectId];
      return subjectChapters && subjectChapters.length > 0
        ? subjectChapters[0].id
        : "";
    };

    // Helper function to create chapter filter object
    const createChapterFilter = (
      chapter: any,
      psSubject: any,
      hasChapterSelected: boolean,
      defaultChapterId: string,
    ) => {
      const psChapter = psSubject?.chapters?.find(
        (ch: any) => ch.chapterId === chapter.id,
      );

      const isChapterSelected = hasChapterSelected
        ? (psChapter?.isSelected ?? false)
        : chapter.id === defaultChapterId;

      return {
        chapterId: chapter.id,
        isSelected: isChapterSelected,
        [FilterType.PAGE]: psChapter?.[FilterType.PAGE] ?? 0,
        [FilterType.ATTEMPT_STATUS]:
          psChapter?.[FilterType.ATTEMPT_STATUS] ?? [],
        [FilterType.DIFFICULTY]: psChapter?.[FilterType.DIFFICULTY] ?? [],
        [FilterType.PATTERN]: psChapter?.[FilterType.PATTERN] ?? [],
        [FilterType.FEATURE]:
          psChapter?.[FilterType.FEATURE] ?? QuestionCategoryType.FOUNDATIONS,
      };
    };

    const hasAnySelectedSubject = hasSelectedSubject();
    const defaultSubjectId = getDefaultSubjectId();

    return {
      subjects: subjects.map(subject => {
        const psSubject = Array.isArray(psFilters?.subjects)
          ? psFilters.subjects.find((ps: any) => ps.subjectId === subject.id)
          : null;

        const isSubjectSelected = hasAnySelectedSubject
          ? (psSubject?.isSelected ?? false)
          : subject.id === defaultSubjectId;

        const hasChapterSelected = hasSelectedChapter(psSubject);
        const defaultChapterId = getDefaultChapterId(subject.id);

        return {
          subjectId: subject.id,
          isSelected: isSubjectSelected,
          chapters:
            chapters[subject.id]?.map(chapter =>
              createChapterFilter(
                chapter,
                psSubject,
                hasChapterSelected,
                defaultChapterId,
              ),
            ) ?? [],
        };
      }),
    };
  }

  return {
    subjects,
    chapters,
    topics,
    patterns,
    difficulty,
    attemptStatus,
    pyqMock: {
      years:
        res.previous_years?.map((item, idx) => ({
          ...item,
          isSelected: idx === 0,
        })) ?? [],
    },
    problemsetFilters: getProblemsetFilters(),
  };
}

function getSubjects(res: GetExamConfigApiResponse) {
  return (
    res.subjects?.map(item => ({
      id: item.slug,
      viewModel: {
        id: item.slug,
        name: item.name,
        icon: item?.metadata?.icon,
        color: item?.metadata?.color,
        altText: item?.metadata?.altText,
      },
    })) ?? []
  );
}

function getChapters(res: GetExamConfigApiResponse) {
  return Object.fromEntries(
    Object.entries(res.chapters ?? {}).map(([key, value]) => [
      key,
      value.map(item => ({
        id: item.slug,
        viewModel: {
          id: item.slug,
          name: item.name,
          icon: item?.metadata?.icon,
          color: item?.metadata?.color,
        },
        questionsCount: item.questions_count,
        topicsCount: item.topics_count,
      })),
    ]),
  );
}

function getPatterns(res: GetExamConfigApiResponse) {
  return (
    res.question_types?.map(item => ({
      id: item.reference_id,
      viewModel: {id: item.reference_id, name: item.name},
    })) ?? []
  );
}

function getDifficulty(res: GetExamConfigApiResponse) {
  return (
    res.difficulty_types?.map(item => {
      const type = stringToProblemDifficultyType(item.reference_id);
      return {
        id: item.reference_id,
        viewModel: {
          id: type,
          name: item.name,
          color: difficultyTheme[type],
        },
      };
    }) ?? []
  );
}

function getAttemptStatus(res: GetExamConfigApiResponse) {
  return (
    res.user_question_status?.map(item => {
      const type = stringToAttemptStatusType(item.reference_id);
      return {
        id: item.reference_id,
        viewModel: {
          id: type,
          name: item.name,
          color: statusTheme[type],
          icon: statusIcons[type] ?? undefined,
        },
      };
    }) ?? []
  );
}

export const examConfigApiSlice = createApi({
  reducerPath: "examConfigApi",
  baseQuery: axiosBaseQuery(),
  tagTypes: ["User", "Exam"],
  endpoints: builder => ({
    getExamConfig: builder.query<IGetExamConfig, void>({
      query: () => ({
        url: "/core/content/",
        method: "GET",
      }),
      transformResponse: (response: GetExamConfigApiResponse) =>
        parseGetExamConfigResponse(response),
      providesTags: ["Exam", "User"],
    }),
    updateProblemsetFilters: builder.mutation<
      void,
      {
        subjectId: string;
        chapterId: string;
        [FilterType.FEATURE]?: QuestionCategoryType;
        [FilterType.PAGE]?: number;
        [FilterType.ATTEMPT_STATUS]?: AttemptStatusType[];
        [FilterType.DIFFICULTY]?: ProblemDifficultyType[];
        [FilterType.PATTERN]?: ProblemType[];
      }
    >({
      queryFn: async (
        {
          subjectId,
          chapterId,
          [FilterType.FEATURE]: feature,
          [FilterType.PAGE]: page,
          [FilterType.ATTEMPT_STATUS]: attemptStatus,
          [FilterType.DIFFICULTY]: difficulty,
          [FilterType.PATTERN]: pattern,
        },
        api,
      ) => {
        api.dispatch(
          examConfigApiSlice.util.updateQueryData(
            "getExamConfig",
            undefined,
            draft => {
              draft.problemsetFilters.subjects.forEach(subject => {
                const isSubjectSelected = subject.subjectId === subjectId;
                subject.isSelected = isSubjectSelected;
                if (!isSubjectSelected) return;

                subject.chapters.forEach(chapter => {
                  const isChapterSelected = chapter.chapterId === chapterId;
                  chapter.isSelected = isChapterSelected;
                  if (isChapterSelected) {
                    if (feature) chapter[FilterType.FEATURE] = feature;
                    if (page) chapter[FilterType.PAGE] = page;
                    if (attemptStatus)
                      chapter[FilterType.ATTEMPT_STATUS] = attemptStatus;
                    if (difficulty) chapter[FilterType.DIFFICULTY] = difficulty;
                    if (pattern) chapter[FilterType.PATTERN] = pattern;
                  }
                });
              });
              localStorage.setItem(
                "problemsetFilters",
                JSON.stringify(draft.problemsetFilters),
              );
            },
          ),
        );
        return {data: undefined};
      },
    }),
  }),
});

export const {useGetExamConfigQuery, useUpdateProblemsetFiltersMutation} =
  examConfigApiSlice;
